// ═══════════════════════════════════════════════════════════════
// ShellNav.jsx — verticalstack left navigation
// Path: src/components/ShellNav.jsx
//
// S10.B (Jul 8, 2026) — EDIT. Flip XC-CAL row at index 2:
//   layer1Active: false → true. Remove lockReason. XC-CAL (Calendar)
//   ships this session. 6 of 9 Cross-cutting nav items now unlocked.
//
// S9.A (June 30, 2026) — EDIT. Flip XC-MKT row at index 1:
//   layer1Active: false → true. Remove lockReason. XC-MKT (Marketplace)
//   ships this session. 5 of 9 Cross-cutting nav items now unlocked.
//
// S8.B (June 29, 2026) — EDIT. Flip XC-NET row at index 0:
//   layer1Active: false → true. Remove lockReason. XC-NET (Network)
//   ships this session. 4 of 9 Cross-cutting nav items now unlocked.
//   Also tightened xc-marketplace lockReason (S8/S9 → S9; Network is
//   S8, Marketplace is S9).
//
// HISTORY
//   S31 LAYER 1 (Apr 27, 2026) — NEW FILE for sandbox-schema migration.
//   S4.B (May 17, 2026) — Cross-cutting nav block (9 universal items).
//   S5.B (May 18, 2026) — Flip xc-documents active (XC-DOC ships).
//   S6.B (May 19, 2026) — Flip xc-messages active (XC-MSG ships).
//   S7.A (May 21, 2026) — Flip xc-team active (XC-TEAM ships).
//   S8.B (Jun 29, 2026) — Flip xc-network active (XC-NET ships).
//   S9.A (Jun 30, 2026) — Flip xc-marketplace active (XC-MKT ships).
//   S10.B (Jul 8, 2026) — Flip xc-calendar active (XC-CAL ships).
//
// LAYOUT
//   Left rail. Two groups:
//     1. STAGE_NAV — 7 lifecycle stages (Acquire / Develop / Operate /
//        Maintain / Reposition / Realize / Hold)
//     2. XC_NAV_ITEMS — 9 cross-cutting platform components
//        (universal, identical on every stage)
//
//   Each item has:
//     - id          — section key (used by Shell to dispatch content)
//     - label       — display text
//     - tag         — XC-* code for analytics/canon mapping
//     - layer1Active — boolean; if false, renders locked with lockReason
//     - lockReason  — explainer string when locked (S-N preview text)
//
// ROUTING
//   Click → onSectionChange(id). Shell handles dispatch.
//   Locked items show a tooltip and the lockReason; click is a no-op
//   (or fires an info toast — Shell's call).
// ═══════════════════════════════════════════════════════════════

import React from 'react';

// ── Lifecycle stage navigation (top group) ───────────────────────────────
const STAGE_NAV = [
  { id: 'overview',   label: 'Overview',   tag: 'OVERVIEW' },
  { id: 'acquire',    label: 'Acquire',    tag: 'STG-ACQ' },
  { id: 'develop',    label: 'Develop',    tag: 'STG-DEV' },
  { id: 'operate',    label: 'Operate',    tag: 'STG-OPR' },
  { id: 'maintain',   label: 'Maintain',   tag: 'STG-MAINT' },
  { id: 'reposition', label: 'Reposition', tag: 'STG-REPO' },
  { id: 'realize',    label: 'Realize',    tag: 'STG-REAL' },
  { id: 'hold',       label: 'Hold',       tag: 'STG-HOLD' },
];

// ── Cross-cutting platform components (bottom group, 9 items) ────────────
// Updated per S5 (xc-documents), S6 (xc-messages), S7 (xc-team),
// S8 (xc-network), S9 (xc-marketplace), S10 (xc-calendar) ship. 6 of 9 unlocked.
const XC_NAV_ITEMS = [
  // S8.B flip — XC-NET ships
  { id: 'xc-network',     label: 'Network',     tag: 'XC-NET',    layer1Active: true,  lockReason: null },
  // S9.A flip — XC-MKT ships
  { id: 'xc-marketplace', label: 'Marketplace', tag: 'XC-MKT',    layer1Active: true,  lockReason: null },
  // S10.B flip — XC-CAL ships
  { id: 'xc-calendar',    label: 'Calendar',    tag: 'XC-CAL',    layer1Active: true,  lockReason: null },
  { id: 'xc-triggers',    label: 'Triggers',    tag: 'XC-TRIG',   layer1Active: false, lockReason: 'Coming in S11 — automation rules across the lifecycle' },
  { id: 'xc-documents',   label: 'Documents',   tag: 'XC-DOC',    layer1Active: true,  lockReason: null },
  { id: 'xc-messages',    label: 'Messages',    tag: 'XC-MSG',    layer1Active: true,  lockReason: null },
  { id: 'xc-reports',     label: 'Reports',     tag: 'XC-RPT',    layer1Active: false, lockReason: 'Coming in S12 — owner reports, KPIs, exports' },
  { id: 'xc-ai',          label: 'AI Assist',   tag: 'XC-AI',     layer1Active: false, lockReason: 'Coming in later sessions — embedded across the lifecycle' },
  { id: 'xc-team',        label: 'Team',        tag: 'XC-TEAM',   layer1Active: true,  lockReason: null },
];

// ── Inline styles (matching Shell.jsx neutral professional) ──────────────
const styles = {
  nav: {
    width: 220,
    minWidth: 220,
    backgroundColor: '#FFFFFF',
    borderRight: '1px solid #E5E7EB',
    padding: '16px 0',
    overflowY: 'auto',
    fontFamily: 'Arial, sans-serif',
    fontSize: 13,
    flexShrink: 0,
  },
  groupLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: '#9CA3AF',
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    padding: '8px 20px',
    marginTop: 8,
  },
  item: {
    display: 'block',
    width: '100%',
    textAlign: 'left',
    padding: '8px 20px',
    border: 'none',
    background: 'transparent',
    fontFamily: 'inherit',
    fontSize: 13,
    color: '#1F2937',
    cursor: 'pointer',
    borderLeft: '3px solid transparent',
    transition: 'background-color 80ms',
  },
  itemActive: {
    backgroundColor: '#F0F4F8',
    color: '#003060',
    fontWeight: 700,
    borderLeft: '3px solid #003060',
  },
  itemLocked: {
    color: '#9CA3AF',
    cursor: 'not-allowed',
  },
  lockBadge: {
    display: 'inline-block',
    marginLeft: 6,
    fontSize: 10,
    color: '#9CA3AF',
  },
  divider: {
    height: 1,
    backgroundColor: '#F1F5F9',
    margin: '16px 0',
  },
};

// ── Main component ───────────────────────────────────────────────────────

export default function ShellNav({ currentSection, onSectionChange }) {
  const renderItem = (item) => {
    const isActive = currentSection === item.id;
    const isLocked = item.layer1Active === false;
    const itemStyle = {
      ...styles.item,
      ...(isActive ? styles.itemActive : {}),
      ...(isLocked ? styles.itemLocked : {}),
    };
    return (
      <button
        key={item.id}
        type="button"
        style={itemStyle}
        onClick={() => !isLocked && onSectionChange(item.id)}
        disabled={isLocked}
        title={isLocked ? item.lockReason || '' : item.label}
        aria-label={item.label + (isLocked ? ' (locked)' : '')}
        data-tag={item.tag}
      >
        {item.label}
        {isLocked && <span style={styles.lockBadge}>🔒</span>}
      </button>
    );
  };

  return (
    <nav style={styles.nav} aria-label="Primary navigation">
      <div style={styles.groupLabel}>Lifecycle</div>
      {STAGE_NAV.map(renderItem)}

      <div style={styles.divider} />

      <div style={styles.groupLabel}>Platform</div>
      {XC_NAV_ITEMS.map(renderItem)}
    </nav>
  );
}

// Export item lists for tests / harnesses
export { STAGE_NAV, XC_NAV_ITEMS };
